import * as HLF from "html-living-framework";
import { Expo, Power2, TimelineLite, TimelineMax, TweenMax, SlowMo } from 'gsap';
import {CustomEase} from "vendor/CustomEase";
// @ts-ignore
import { PixiPlugin } from "gsap/PixiPlugin";
import { StoryLoadScreen } from "States/StoryLoadScreen"
import { Application } from "Singletons/Application";
import { Button } from "GameObjects/Button";
import * as Constants from "Data/Constants";
import * as Assets from "Data/Assets";
import * as Topics from "Data/Topics";
import { SceneSprite } from "GameObjects/StoryGame/SceneSprite";
import TutorialHand from "GameObjects/StoryGame/TutorialHand";
import { makeText, sendToFront } from "html-living-framework/lib/Utils/PixiUtils";
const dontTreeShakeMe = [PixiPlugin];

type TRANSITION_TYPES = "fade" | "left" | "right" | "up" | "down" | "ontop" | "behind";

export abstract class AbstractStoryState extends HLF.State {

	public mask!: PIXI.Graphics | null;
	protected debugText?: PIXI.BitmapText;
	protected _alphaFilter?: PIXI.filters.AlphaFilter;
	protected _back: string[] = [];
	protected backButton?: Button;
	protected pauseButton?: Button;
    protected _subscriptions: any[] = [];

	protected frameLeft?: SceneSprite;
	protected frameRight?: SceneSprite;

	protected _next: string;
	protected _transition: TRANSITION_TYPES = "fade";
	protected animateInTimeline?: TimelineLite | TimelineMax;
	protected animateOutTimeline?: TimelineLite | TimelineMax;

	protected dontScaleUp = false; // HACK

	protected tiledBG = new PIXI.TilingSprite(HLF.PixiUtils.getTexture("tiled-bg"));
	protected _tutorialHand?: TutorialHand;

	protected isPreloading: boolean = false;
	protected stalled: boolean = false;
	private borderTEMP?: PIXI.Graphics; // TEMPORARY

	public constructor(pData: {next: string}) {
		super();
		this._next = pData.next;
		// this.filters = [this._alphaFilter];
		this.filterArea = Application.instance.screen;
	}

	public onResize(pSize: PIXI.Point) {
		super.onResize(pSize);
		const {x:width, y:height} = pSize;
		const offsetLeft = (width - Constants.SCENE_WIDTH) / 2;
		this.pivot.set(Constants.SCENE_WIDTH / 2, Constants.SCENE_HEIGHT / 2);
		this.position.set(pSize.x / 2, pSize.y / 2);
		if (this.backButton) {
			// bottom left
			this.backButton.position.set(
				-offsetLeft + (this.backButton.width / 2),
				pSize.y - (this.backButton.height / 2),
			);
		}

		if (this.pauseButton) {
			// top right
			this.pauseButton.position.set(
				pSize.x - offsetLeft - (this.pauseButton.width / 2),
				(this.pauseButton.height / 2),
			);
		}

		if (this.debugText) {
			// sendToFront(this.debugText);
			this.debugText.position.x = pSize.x - offsetLeft - this.debugText.textWidth;
			this.debugText.position.y = pSize.y - this.debugText.textHeight;
		}

		if (this.frameLeft) {
			this.frameLeft.position.x = (Constants.SCENE_WIDTH - pSize.x) / 2;
		}
		if (this.frameRight) {
			this.frameRight.position.x = (Constants.SCENE_WIDTH - ((Constants.SCENE_WIDTH - pSize.x) / 2));
		}

		const borderWidth = 5;
		if (!this.mask) {
			this.mask = this.addChild(new PIXI.Graphics());
		}
		this.mask.clear();
		this.mask.beginFill(0xff000);
		this.mask.drawRect(-offsetLeft - borderWidth, 0 - borderWidth, pSize.x + borderWidth * 2, pSize.y + borderWidth * 2);
		this.mask.endFill();

		if (!this.borderTEMP) {
			this.borderTEMP = this.addChild(new PIXI.Graphics());
		}
		this.borderTEMP.clear();
		this.borderTEMP.lineStyle(borderWidth, 0x000000, 1, 1);
		this.borderTEMP.drawRect(-offsetLeft, 0, pSize.x, pSize.y);

		// HLF.LogUtils.log("resize: %s x %s", {className: this.name, color: HLF.LogUtils.COLOR_LIGHT_BLUE}, pSize.x, pSize.y);
		this.tiledBG.width = pSize.x;
		this.tiledBG.height = pSize.y;
	}

	public init(pSize: PIXI.Point, pData?: {name: string, back?: string[], transition?: TRANSITION_TYPES}) {
		if (pData) {
			this.name = pData.name;
			this.debugText = this.addChild(makeText(this.name, Constants.FONT_GEL_MED_35, 35));
			this.debugText.alpha = 0.5;
			this._back = pData.back ?? [];
			this._transition = pData.transition ?? "fade";
			if (this._back.length) {
				this.backButton = new Button(this.gotoPreviousState.bind(this), "button-default", ["ui-1", "ui-2"]);
				this.backButton.addText("Back", Constants.FONT_GEL_MED_35, 96);
				this.backButton.alpha = 0.25;
				this.addChild(this.backButton);
			}
		}
		this._tutorialHand = this.addChild(new TutorialHand());
		// HLF.LogUtils.log('init', {className: this.name, color: HLF.LogUtils.COLOR_LIGHT_BLUE});
		if (this.dontScaleUp || this._transition === "left" || this._transition === "right" || this._transition === "up" || this._transition === "down") {
			this.scale.set(this.scale.x * 0.85);
		}

		this.pauseButton = new Button(this.onPauseButtonClicked.bind(this), "button-default", ["ui-1", "ui-2"]);
		this.pauseButton.scale.set(0.5);
		this.pauseButton.addText("Pause", Constants.FONT_GEL_MED_35, 96, 0xffffff);
		this.pauseButton.sprite!.tint = 0x7cc600;
		// this.pauseButton.alpha = 0.5;

		this.addChild(this.pauseButton);
		// super.init calls onResize
		super.init(pSize, pData);
	}

	public animateIn(pOnComplete: () => void) {
		this.animateInTimeline?.kill();
		this.animateOutTimeline?.kill();
		HLF.LogUtils.log("animate in", {className: this.name, color: HLF.LogUtils.COLOR_LIGHT_BLUE});
		super.animateIn(()=>{
			pOnComplete();
			this.preloadNextState();
		});
		const t = this.animateInTimeline = new TimelineMax({ onComplete: this.onAnimateInComplete.bind(this)});

		const ease = CustomEase.create("custom", "M0,0 C0.26,0 0.348,0.046 0.5,0.5 0.655,0.965 0.818,1.001 1,1 ", {});
		const slideDuration = 1.75;
		const zoomDuration = 1;

		if (this._transition === "left"
		 || this._transition === "right"
		 || this._transition === "up"
		 || this._transition === "down") {
			if (this.borderTEMP) this.borderTEMP.visible = true;
			this.tiledBG.setParent(this.parent);
			this.tiledBG.parent.setChildIndex(this.tiledBG, 0);
			this.tiledBG.visible = true;
			t.delay(zoomDuration);
			switch (this._transition) {
				case "left":
					t.from(this, slideDuration, {pixi: {x: this.x + this._size.x }, ease});
					break;
				case "right":
					t.from(this, slideDuration, {pixi: {x: this.x - this._size.x }, ease});
					break;
				case "up":
					t.from(this, slideDuration, {pixi: {y: this.y + this._size.y }, ease});
					break;
				case "down":
					t.from(this, slideDuration, {pixi: {y: this.y - this._size.y }, ease});
					break;
			}
			if (this.dontScaleUp) {
				// this.scale.set(this.scale.x * 0.85);
			}
			else {
				t.to(this, 1, {pixi: {scale: 1}, ease: Expo.easeOut});
			}
		}
		else if (this._transition === "fade") {
			if (this._alphaFilter === undefined) {
				this._alphaFilter = new PIXI.filters.AlphaFilter(0);
				this.filters = [this._alphaFilter];
			}
			t.to(this._alphaFilter, 1, {alpha: 1});
		}
	}

	public animateOut(pOnComplete: () => void) {
		this.animateInTimeline?.kill();
		this.animateOutTimeline?.kill();
		const t = this.animateInTimeline = new TimelineMax({onComplete: ()=>{
			super.animateOut(pOnComplete);
		}});
		const ease = CustomEase.create("custom", "M0,0 C0.26,0 0.348,0.046 0.5,0.5 0.655,0.965 0.818,1.001 1,1 ", {});
		const slideDuration = 1.75;
		const zoomDuration = 1;

		if (this._transition === "left"
		 || this._transition === "right"
		 || this._transition === "up"
		 || this._transition === "down") {
			if (this.borderTEMP) this.borderTEMP.visible = true;
			this.tiledBG.setParent(this.parent);
			this.tiledBG.parent.setChildIndex(this.tiledBG, 0);
			this.tiledBG.visible = true;
			if (this.dontScaleUp) {
				// this.scale.set(this.scale.x * 0.85);
				t.delay(zoomDuration);
			}
			else {
				t.to(this, zoomDuration, {pixi: {
					scale: 0.85,
				}, ease: Expo.easeOut});
			}
			switch (this._transition) {
				case "left":
					t.to(this, slideDuration, {pixi: {x: this.x - this._size.x }, ease});
					break;
				case "right":
					t.to(this, slideDuration, {pixi: {x: this.x + this._size.x }, ease});
					break;
				case "up":
					t.to(this, slideDuration, {pixi: {y: this.y - this._size.y }, ease});
					break;
				case "down":
					t.to(this, slideDuration, {pixi: {y: this.y + this._size.y }, ease});
					break;
			}
		}
		else if (this._transition === "fade" || this._transition === "behind") {
			this.tiledBG.visible = false;
			if (this._alphaFilter === undefined) {
				this._alphaFilter = new PIXI.filters.AlphaFilter(1);
				this.filters = [this._alphaFilter];
			}
			switch (this._transition) {
				case "fade":
					t.to(this._alphaFilter, 0.5, {alpha: 0});
					break;
				case "behind":
					t.to(this._alphaFilter, 1.5, {alpha: 0});
					break;
			}
		}
	}

	public gotoNextState(pData?: {transition: TRANSITION_TYPES}) {
		PubSub.publishSync(Topics.HIDE_ALL_HINTS, undefined);
		this.interactiveChildren = false;
		this.interactive = false;
		this.buttonMode = false;
		this.accessible = false;
		this._tutorialHand?.destroy();
		this.backButton?.hide(true);
		let steps: HLF.TransitionStep[] = [];
		this._transition = pData?.transition ?? "fade";
		switch (this._transition) {
			case "left":
			case "right":
			case "up":
			case "down":
				steps = [
					HLF.TransitionStep.LoadAssets,
					HLF.TransitionStep.AttachNewBehind,
					HLF.TransitionStep.PauseAFrame,
					HLF.TransitionStep.AnimSimultaneously,
					HLF.TransitionStep.RemoveCurrent,
					HLF.TransitionStep.UnloadAssets,
				];
				break;
			case "ontop":
				steps = [
					HLF.TransitionStep.LoadAssets,
					HLF.TransitionStep.AttachNewInFront,
					// HLF.TransitionStep.PauseAFrame,
					HLF.TransitionStep.AnimSimultaneously,
					HLF.TransitionStep.RemoveCurrent,
					HLF.TransitionStep.UnloadAssets,
				];
				break;
			case "behind":
				steps = [
					HLF.TransitionStep.LoadAssets,
					HLF.TransitionStep.AttachNewBehind,
					// HLF.TransitionStep.PauseAFrame,
					HLF.TransitionStep.AnimSimultaneously,
					HLF.TransitionStep.RemoveCurrent,
					HLF.TransitionStep.UnloadAssets,
				];
				break;
			case "fade":
			default:
				steps = [
					HLF.TransitionStep.AnimCurrentOut,
					HLF.TransitionStep.RemoveCurrent,
					HLF.TransitionStep.UnloadAssets,
					HLF.TransitionStep.LoadAssets,
					HLF.TransitionStep.AttachNewInFront,
					HLF.TransitionStep.AnimNewIn,
				];
				break;
		}

		const go = ()=>{
			if (this.dontScaleUp) {
				console.timeEnd(`hold on static screen ${this.name}`);
			}
			PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken(
				{id: this._next, data: {
					name: this._next,
					back: [...this._back, this.name], // add this state to the history
					...pData,
				}}, StoryLoadScreen.NAME, ...steps)
			);
		}

		if (this.isPreloading) {
			HLF.LogUtils.logWarning("Transition stalled -- still preloading assets for %s", {className: this.name, color: HLF.LogUtils.COLOR_RELISH}, this._next);
			this.stalled = true;
		}
		else {
			go();
		}
	}

	public showHint(
        target: PIXI.Container | PIXI.Point | PIXI.IPointData,
		type: "hand" | "pulse" = "pulse",
		delay: number = 0,
	) {
		if (delay) {
			HLF.LogUtils.log("show hint %s seconds from now", {className: this.name, color: HLF.LogUtils.COLOR_RELISH}, delay);
		}
		PubSub.publishSync(Topics.SHOW_HINT, {type, target, delay});
	}

	public hideHint(target?: PIXI.Container) {
		PubSub.publishSync(Topics.HIDE_HINT, target);
	}

	public gotoPreviousState() {
		Application.instance.loader.reset(); // cancel any preloading
		PubSub.publishSync(Topics.HIDE_ALL_HINTS, undefined);
		this.animateInTimeline?.kill();
		this.animateOutTimeline?.kill();
		this.interactiveChildren = false;
		this.interactive = false;
		this.buttonMode = false;
		this.accessible = false;
		const stateToLoad = this._back.pop()!;
		const steps = [
            HLF.TransitionStep.ShowLoadScreen,
            HLF.TransitionStep.RemoveCurrent,
			HLF.TransitionStep.UnloadAssets,
            HLF.TransitionStep.LoadAssets,
            HLF.TransitionStep.AttachNewBehind,
            HLF.TransitionStep.AnimNewIn,
            HLF.TransitionStep.HideLoadScreen,
        ];
        PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken(
			{id: stateToLoad, data: {
				name: stateToLoad,
				back: this._back, // don't add this state to the history
				transition: "fade",
			}}, StoryLoadScreen.NAME, ...steps)
		);
	}

	public destroy(pDestroyOptions?: any) {
		this.tiledBG.setParent(this);
        super.destroy(pDestroyOptions);
		this._subscriptions.forEach(PubSub.unsubscribe);
		this.animateInTimeline?.kill();
		this.animateOutTimeline?.kill();
    }

	protected onPauseButtonClicked() {
		PubSub.publish(Topics.PAUSE_GAME, undefined);
	}

	protected onAnimateInComplete() {
		if (this.dontScaleUp) {
			console.log(`begin hold on static screen ${this.name}`);
			console.time(`hold on static screen ${this.name}`);
		}
		else {
			if (this.borderTEMP) this.borderTEMP.visible = false;
			if (this.tiledBG) this.tiledBG.visible = false;
		}
		HLF.LogUtils.log("animate in complete", {className: this.name, color: HLF.LogUtils.COLOR_LIGHT_BLUE});
		const _size = Application.instance.resizer.getSize();
		if (!_size.equals(this._size)) {
			this.onResize(this._size);
		}
	}

	protected preloadNextState() {
		HLF.LogUtils.log("begin preloading assets for %s", {className: this.name, color: HLF.LogUtils.COLOR_LIGHT_BLUE}, this._next);
		this.isPreloading = true;
		console.time(`preload assets for ${this._next}`);
		PubSub.publishSync(HLF.LOAD_ASSETS, {assets: HLF.AssetMap.getAssetGroup(this._next), callback: ()=>{
			this.isPreloading = false;
			console.timeEnd(`preload assets for ${this._next}`);
			if (this.stalled) {
				this.gotoNextState();
			}
			HLF.LogUtils.log("finished preloading assets for %s", {className: this.name, color: HLF.LogUtils.COLOR_LIGHT_BLUE}, this._next);
		}});
	}

	protected makeParallax(
		layers: (PIXI.Container & {z: number})[],
		pOptions: {
			duration: number,
			distance: number,
			spread: number,
			center?: {x: number, y: number},
			direction?: "in" | "out",
		}
	) {
        const t = new TimelineLite();
		const ease = Expo.easeOut;
		const duration = pOptions.duration;
		const distance = pOptions.distance;
		const spread = pOptions.spread;
		const center = pOptions?.center ?? {x: 0.5, y: 0.75};
		const direction = pOptions?.direction ?? "in";

		const containerPos = {
			x: (Constants.SCENE_WIDTH * center.x),
			y: (Constants.SCENE_HEIGHT * center.y),
		};

		// parallax
		for (let i = layers.length-1; i >= 0; --i) {
			const siblingIndex = this.getChildIndex(layers[i]);
			const child = this.removeChild(layers[i]);
			const container = this.addChildAt(new PIXI.Container(), siblingIndex);
			container.name = child.name + "_parallax";
			container.position.set(containerPos.x, containerPos.y);
			child.setParent(container);
			HLF.PointUtils.subtractFromPoint(child.position, container.position);
			const fromScale = 1 + ((distance - child.z) / spread);
			t.fromTo(container, duration, {pixi: {scale: fromScale}}, {pixi: {scale: 1}, ease}, 0);
		};
	}

}
